import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine, Reveal } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import { api } from "../lib/api";

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yArt = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yWord = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  useEffect(() => {
    api.get("/products").then((all) => {
      const feat = all.filter((p) => p.featured);
      setFeatured((feat.length ? feat : all).slice(0, 4));
    }).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      {/* ------- HERO : bannière centrale Moha66 ------- */}
      <section ref={heroRef} className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden grain px-4 pt-24 pb-16">
        <motion.div style={{ y: yWord, opacity }} className="absolute inset-0 flex items-center justify-center pointer-events-none select-none" aria-hidden="true">
          <span className="font-display font-extrabold uppercase text-outline text-[34vw] sm:text-[26vw] leading-none tracking-tighter whitespace-nowrap">66</span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="relative z-10 font-mono text-[10px] sm:text-xs uppercase tracking-[0.45em] text-neutral-400 text-center"
        >
          Streetwear · Qualité Premium
        </motion.p>

        <h1 className="relative z-10 mt-6 text-center font-display font-extrabold uppercase leading-[0.86] tracking-tighter text-white text-[13.5vw] sm:text-8xl lg:text-[9rem]">
          <MaskedLine delay={0.15}>MOHA<span className="text-gold">66</span></MaskedLine>
        </h1>

        <motion.div style={{ y: yArt }} className="relative z-10 mt-8 sm:mt-10">
          <motion.img
            src="/brand/moha66-mark.jpg"
            alt="Logo Moha66 — artwork doré signature"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="w-[min(72vw,360px)] mix-blend-screen gold-glow animate-float-slow"
            data-testid="hero-brand-artwork"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="relative z-10 mt-8 max-w-md text-center text-sm sm:text-base text-neutral-400 leading-relaxed"
        >
          L'attitude du 66, portée partout. Pièces monochromes en matière lourde,
          coupes oversize, tailles XS au 4XL.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="relative z-10 mt-8 flex flex-col sm:flex-row items-center gap-4"
        >
          <Link
            to="/boutique"
            data-testid="hero-cta-shop"
            className="bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] px-10 py-4 hover:bg-gold transition-colors duration-300"
          >
            Découvrir la boutique
          </Link>
          <Link
            to="/livraison"
            data-testid="hero-cta-delivery"
            className="border border-lineActive text-white font-mono text-xs uppercase tracking-[0.25em] px-10 py-4 hover:border-white hover:bg-white/5 transition-all duration-300"
          >
            Livraison & retours
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2"
          aria-hidden="true"
        >
          <div className="w-px h-10 bg-gradient-to-b from-white/60 to-transparent" />
        </motion.div>
      </section>

      {/* ------- MARQUEU ÉDITORIAL ------- */}
      <Marquee items={["Moha66", "Streetwear", "S — XXL", "Livraison mondiale", "Paiement sécurisé", "Qualité premium"]} />

      {/* ------- SÉLECTION BENTO ------- */}
      <section className="px-4 sm:px-8 lg:px-16 py-16 sm:py-24 lg:py-32" data-testid="featured-section">
        <div className="flex items-end justify-between gap-6 mb-10 sm:mb-14">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">La sélection</p>
            <h2 className="mt-3 font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">
              Pièces <span className="text-outline-strong">essentielles</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/boutique" data-testid="featured-see-all" className="hidden sm:inline-block font-mono text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-white border-b border-lineActive hover:border-white pb-1 transition-colors">
              Tout voir
            </Link>
          </Reveal>
        </div>

        {featured.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08} className={["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"][i % 4]}>
                <ProductCard product={p} fill />
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* ------- MANIFESTE ------- */}
      <section className="relative border-y border-line bg-coal overflow-hidden" data-testid="manifesto-section">
        <div className="px-4 sm:px-8 lg:px-16 py-16 sm:py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">Le manifeste</p>
            <h2 className="mt-6 font-display font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-white">
              Pensée pour la rue.
              <br />
              <span className="text-outline-strong">Portée partout.</span>
            </h2>
          </Reveal>
          <div className="flex flex-col justify-end gap-8">
            <Reveal delay={0.1}>
              <p className="text-sm sm:text-base text-neutral-400 leading-relaxed max-w-lg">
                Moha66, c'est un vestiaire sans compromis : noir, blanc, gris, matière lourde et détails précis.
                Chaque pièce est pensée pour durer, se porter seule ou se superposer.
              </p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-line border border-line">
              {[
                { t: "Matières", d: "Coton lourd, molleton gratté, finitions renforcées." },
                { t: "Coupes", d: "Oversize maîtrisée, du XS au 4XL." },
                { t: "Esprit", d: "Monochrome, franc, sans artifice." },
              ].map((v, i) => (
                <Reveal key={v.t} delay={0.15 + i * 0.08} className="bg-coal p-6">
                  <h3 className="font-display font-bold uppercase text-sm tracking-wider text-white">{v.t}</h3>
                  <p className="mt-2 text-xs text-neutral-400 leading-relaxed">{v.d}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ------- CTA FINALE ------- */}
      <section className="px-4 sm:px-8 lg:px-16 py-20 sm:py-28 text-center overflow-hidden" data-testid="home-cta-section">
        <Reveal>
          <h2 className="font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl text-white leading-none">
            Prêt à porter <span className="text-gold">l'attitude ?</span>
          </h2>
          <Link
            to="/boutique"
            data-testid="home-cta-final-button"
            className="mt-10 inline-block bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] px-12 py-4 hover:bg-gold transition-colors duration-300"
          >
            Voir toute la boutique
          </Link>
        </Reveal>
      </section>
    </main>
  );
};

export default Home;
